import { produce } from 'immer';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resources } from '../../apps/benefit/resources/resources';
import { getUniqErrorMessages } from '../global/helpers';
import Icons from './Icons';

export enum NotificationType {
  Error = 1,
  Warning = 2,
  Info = 3,
  Tip = 4,
  Success = 5,
}

interface NotificationMessage {
  message: string;
  onShowDetailsClick?: () => void;
  showDetailsTitle?: string;
  showDetailsColor?: string;
}

interface NotificationProps {
  type: NotificationType;
  messages: NotificationMessage[];
  showClose?: boolean;
  onCloseClick?: () => void;
  controlClassName?: string;
  scrollUp?: boolean;
}

interface NotificationState {
  isVisible: boolean;
}

export const Notification = (props: NotificationProps) => {
  const {
    type,
    messages,
    showClose,
    onCloseClick,
    controlClassName,
    scrollUp = true,
  } = props;
  const [state, setState] = useState<NotificationState>({ isVisible: true });

  useEffect(() => {
    if (messages && scrollUp) {
      const element = document.getElementsByTagName('html')[0];
      if (element) {
        element.scroll(0, 0);
      }
    }
  }, [messages, scrollUp]);

  if (!state.isVisible || !messages.length) return null;

  const internalOnCloseClick = showClose
    ? () => {
        onCloseClick?.();
        setState(
          produce(d => {
            d.isVisible = false;
          }),
        );
      }
    : undefined;

  if (type === NotificationType.Error)
    return <ErrorNotification {...props} controlClassName={controlClassName} />;
  if (type === NotificationType.Warning)
    return (
      <WarningNotification
        {...props}
        controlClassName={controlClassName}
        onCloseClick={internalOnCloseClick}
      />
    );
  if (type === NotificationType.Info)
    return (
      <InfoNotification
        {...props}
        controlClassName={controlClassName}
        onCloseClick={internalOnCloseClick}
      />
    );
  if (type === NotificationType.Tip)
    return (
      <TipNotification
        {...props}
        controlClassName={controlClassName}
        onCloseClick={internalOnCloseClick}
      />
    );
  if (type === NotificationType.Success)
    return (
      <SuccessNotification
        {...props}
        controlClassName={controlClassName}
        onCloseClick={internalOnCloseClick}
      />
    );

  return null;
};

interface InternalNotificationProps {
  messages: NotificationMessage[];
  controlClassName?: string;
  onCloseClick?: () => void;
}

const ErrorNotification = (props: InternalNotificationProps) => {
  const { messages, controlClassName } = props;
  const { t: translate } = useTranslation();

  const uniqMessages = getUniqErrorMessages(messages);

  const classNames = `notification n-error ${controlClassName ?? ''}`.trim();

  return (
    <div className={classNames}>
      <div className="notification-icon">
        <Icons.NotificationsError />
      </div>
      <ul className="notification-details list-reset">
        {uniqMessages.map(e => (
          <li key={e.message}>
            <span
              dangerouslySetInnerHTML={{
                __html: _.unescape(e.message),
              }}
            />
            <div className="error-list-btn">
              {!!e.onShowDetailsClick && (
                <a onClick={e.onShowDetailsClick} className="notification-more">
                  <span className="btn-icon">
                    <Icons.NotificationsArrow color={e.showDetailsColor} />
                  </span>
                  <span
                    className="btn-text"
                    style={{ color: e.showDetailsColor }}
                  >
                    {e.showDetailsTitle ?? translate(resources.seeDetails)}
                  </span>
                </a>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const WarningNotification = (props: InternalNotificationProps) => {
  const { messages, onCloseClick, controlClassName } = props;
  const { t: translate } = useTranslation();

  const uniqMessages = getUniqErrorMessages(messages);

  const classNames = `notification n-warning ${controlClassName ?? ''}`.trim();

  return (
    <div className={classNames}>
      <div className="notification-icon">
        <Icons.NotificationsWarning />
      </div>
      <ul className="notification-details list-reset">
        {uniqMessages.map(e => (
          <li key={e.message}>
            <span
              dangerouslySetInnerHTML={{
                __html: _.unescape(e.message),
              }}
            />
            {!!e.onShowDetailsClick && (
              <a onClick={e.onShowDetailsClick} className="notification-more">
                <span className="btn-icon">
                  <Icons.NotificationsArrow color={e.showDetailsColor} />
                </span>
                <span
                  className="btn-text"
                  style={{ color: e.showDetailsColor }}
                >
                  {e.showDetailsTitle ?? translate(resources.seeDetails)}
                </span>
              </a>
            )}
          </li>
        ))}
      </ul>
      {!!onCloseClick && (
        <button onClick={onCloseClick} className="notification-close">
          <Icons.NotificationsClose />
        </button>
      )}
    </div>
  );
};

const InfoNotification = (props: InternalNotificationProps) => {
  const { messages, onCloseClick, controlClassName } = props;
  const { t: translate } = useTranslation();

  const uniqMessages = getUniqErrorMessages(messages);

  const classNames = `notification n-info ${controlClassName ?? ''}`.trim();

  return (
    <div className={classNames}>
      <div className="notification-icon">
        <Icons.NotificationsInfo />
      </div>
      <ul className="notification-details list-reset">
        {uniqMessages.map(e => (
          <li key={e.message}>
            <span
              dangerouslySetInnerHTML={{
                __html: _.unescape(e.message),
              }}
            />
            {!!e.onShowDetailsClick && (
              <a onClick={e.onShowDetailsClick} className="notification-more">
                <span className="btn-icon">
                  <Icons.NotificationsArrow color={e.showDetailsColor} />
                </span>
                <span
                  className="btn-text"
                  style={{ color: e.showDetailsColor }}
                >
                  {e.showDetailsTitle ?? translate(resources.seeDetails)}
                </span>
              </a>
            )}
          </li>
        ))}
      </ul>
      {!!onCloseClick && (
        <button onClick={onCloseClick} className="notification-close">
          <Icons.NotificationsClose />
        </button>
      )}
    </div>
  );
};

const TipNotification = (props: InternalNotificationProps) => {
  const { messages, onCloseClick, controlClassName } = props;
  const { t: translate } = useTranslation();

  const uniqMessages = getUniqErrorMessages(messages);

  const classNames = `notification n-tip ${controlClassName ?? ''}`.trim();

  return (
    <div className={classNames}>
      <div className="notification-icon">
        <Icons.NotificationsTip />
      </div>
      <ul className="notification-details list-reset">
        {uniqMessages.map(e => (
          <li key={e.message}>
            <span
              dangerouslySetInnerHTML={{
                __html: _.unescape(e.message),
              }}
            />
            {!!e.onShowDetailsClick && (
              <a onClick={e.onShowDetailsClick} className="notification-more">
                <span className="btn-icon">
                  <Icons.NotificationsArrow color={e.showDetailsColor} />
                </span>
                <span
                  className="btn-text"
                  style={{ color: e.showDetailsColor }}
                >
                  {e.showDetailsTitle ?? translate(resources.seeDetails)}
                </span>
              </a>
            )}
          </li>
        ))}
      </ul>
      {!!onCloseClick && (
        <button onClick={onCloseClick} className="notification-close">
          <Icons.NotificationsClose />
        </button>
      )}
    </div>
  );
};

const SuccessNotification = (props: InternalNotificationProps) => {
  const { messages, onCloseClick, controlClassName } = props;
  const { t: translate } = useTranslation();

  const uniqMessages = getUniqErrorMessages(messages);

  const classNames = `notification n-success ${controlClassName ?? ''}`.trim();

  return (
    <div className={classNames}>
      <div className="notification-icon">
        <Icons.NotificationsSuccess />
      </div>
      <ul className="notification-details list-reset">
        {uniqMessages.map(e => (
          <li key={e.message}>
            <span
              dangerouslySetInnerHTML={{
                __html: _.unescape(e.message),
              }}
            />
            {!!e.onShowDetailsClick && (
              <a onClick={e.onShowDetailsClick} className="notification-more">
                <span className="btn-icon">
                  <Icons.NotificationsArrow color={e.showDetailsColor} />
                </span>
                <span
                  className="btn-text"
                  style={{ color: e.showDetailsColor }}
                >
                  {e.showDetailsTitle ?? translate(resources.seeDetails)}
                </span>
              </a>
            )}
          </li>
        ))}
      </ul>
      {!!onCloseClick && (
        <button onClick={onCloseClick} className="notification-close">
          <Icons.NotificationsClose />
        </button>
      )}
    </div>
  );
};
